import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  input,
  OnDestroy,
  OnInit,
  signal,
  Type,
  viewChild,
} from '@angular/core';
import { DeviceButtonTemplatesDatasource } from './device-button-templates.datasource';
import { PbxAccountButtonTemplatesService as PbxAccountButtonTemplatesApiService } from '../../../../../../../api/services/pbx-account-button-templates.service';
import { NgIf, AsyncPipe } from '@angular/common';
import { FlexModule } from '@angular/flex-layout';
import { MatButtonModule } from '@angular/material/button';
import { MatPaginator } from '@angular/material/paginator';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { RolesEnum } from '../../../../../../../core/security/roles.enum';
import { AuthService } from '../../../../../../../core/security/auth.service';
import { merge, debounceTime, tap, Subscription, take, finalize } from 'rxjs';
import { BaseGridRequest, PbxAccountButtonTemplateListModel } from '../../../../../../../api/models';
import {
  DeviceButtonTemplateEditDialogComponent,
  DeviceButtonTemplateEditDialogData,
} from '../../device-button-template-edit-dialog/device-button-template-edit-dialog.component';
import { LocalPbxService } from '../../../../../../local-pbx.service';
import { Angular2PromiseButtonModule } from 'angular2-promise-buttons';
import { capitalize } from 'lodash';
import { ButtonTemplateType } from '../../../enums/button-template-type.enum';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-device-button-templates-list',
  standalone: true,
  imports: [
    FlexModule,
    NgIf,
    MatProgressSpinner,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatPaginator,
    AsyncPipe,
    MatButtonModule,
    Angular2PromiseButtonModule,
    MatTooltipModule,
  ],
  templateUrl: './device-button-templates-list.component.html',
  styleUrl: './device-button-templates-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeviceButtonTemplatesListComponent implements OnInit, AfterViewInit, OnDestroy {
  public isExpansionAvailable = input.required<boolean>();

  public hasEditAccess = false;
  public dataSource: DeviceButtonTemplatesDatasource;

  public readonly capitalize = capitalize;
  public readonly templateUsed = signal<string>(null);
  public readonly displayedColumns: string[] = ['name', 'type', 'actions'];

  private readonly _paginator = viewChild(MatPaginator);
  private readonly _sort = viewChild(MatSort);

  private readonly _subscription = new Subscription();

  constructor(
    private readonly _deviceButtonTemplatesApiService: PbxAccountButtonTemplatesApiService,
    private readonly _dialog: MatDialog,
    private readonly _authService: AuthService,
    private readonly _localPbxService: LocalPbxService,
    private readonly _dialogRef: MatDialogRef<DeviceButtonTemplatesListComponent>,
  ) {}

  public ngOnInit(): void {
    this.hasEditAccess = this._authService.hasAccess([RolesEnum.PbxAccounts_Edit, RolesEnum.PbxAccounts_Delete]);
    this.dataSource = new DeviceButtonTemplatesDatasource(this._deviceButtonTemplatesApiService);
  }

  public ngAfterViewInit(): void {
    this._subscription.add(this.subscribeToSortChange());
    this._subscription.add(this.subscribeToSortAndPagination());

    this.loadData();
  }

  public ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  public canTemplateBeUsed(templateType: ButtonTemplateType): boolean {
    if (!this.isExpansionAvailable() && templateType === ButtonTemplateType.Expansion) {
      return false;
    }
    return true;
  }

  public onTemplateEdit(templateId: string): void {
    const dialogRef = this._dialog.open<
      DeviceButtonTemplateEditDialogComponent,
      DeviceButtonTemplateEditDialogData,
      boolean
    >(DeviceButtonTemplateEditDialogComponent, {
      data: {
        templateId,
      },
      width: '800px',
      height: '70%',
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadData();
      }
    });
  }

  public onTemplateUse(template: PbxAccountButtonTemplateListModel): void {
    const { id, type } = template;

    if (!this.canTemplateBeUsed(type as ButtonTemplateType)) {
      return;
    }

    this.templateUsed.set(id);

    this._localPbxService
      .getPbxAccountButtonTemplateById(id)
      .pipe(
        take(1),
        finalize(() => this.templateUsed.set(null)),
      )
      .subscribe({
        next: template => {
          this._dialogRef.close(template);
        },
      });
  }

  public loadData(): void {
    this.dataSource.queryData(this.getDataFilter());
  }

  private getDataFilter(): BaseGridRequest {
    const paginator = this._paginator();
    const sort = this._sort();

    return {
      skip: paginator.pageIndex * paginator.pageSize,
      limit: paginator.pageSize,
      sort: [{ field: sort.active, direction: sort.direction ? sort.direction.toUpperCase() : undefined }],
    };
  }

  private subscribeToSortChange(): Subscription {
    return this._sort().sortChange.subscribe(() => {
      this._paginator().pageIndex = 0;
    });
  }

  private subscribeToSortAndPagination(): Subscription {
    return merge(this._sort().sortChange, this._paginator().page)
      .pipe(
        debounceTime(100),
        tap(() => this.loadData()),
      )
      .subscribe();
  }
}
